import { z } from 'zod';

// ─────────── Fase 4 · Cobrança do SaaS e parceiros comerciais (docs/07, docs/12) ───────────

export const CICLOS = ['mensal', 'anual'] as const;
export type Ciclo = (typeof CICLOS)[number];
export const METODOS_COBRANCA = ['cartao', 'pix', 'boleto'] as const;
export type MetodoCobranca = (typeof METODOS_COBRANCA)[number];
export const NOME_METODO: Record<MetodoCobranca, string> = {
  cartao: 'Cartão de crédito',
  pix: 'Pix',
  boleto: 'Boleto',
};
export const STATUS_FATURA = ['aberta', 'paga', 'vencida', 'cancelada'] as const;
export type StatusFatura = (typeof STATUS_FATURA)[number];

export interface ItemFatura {
  /** ajuste = diferença proporcional de uma troca de plano no meio do período */
  tipo: 'plano' | 'adicional' | 'filial' | 'implantacao' | 'ajuste';
  codigo: string;
  descricao: string;
  valorCentavos: number;
}

export interface EntradaFatura {
  plano: {
    codigo: string;
    nome: string;
    precoMensalCentavos: number;
    precoFilialAdicionalCentavos: number;
  };
  adicionais: { codigo: string; nome: string; precoMensalCentavos: number; porFilial: boolean }[];
  /** filiais ativas da loja (a primeira está no plano) */
  filiais: number;
  ciclo: Ciclo;
  mesesGratisAnual: number;
  /** taxa de implantação ainda não cobrada */
  implantacaoCentavos: number;
}

/**
 * Itens e total da próxima fatura. Anual = 12 meses com `mesesGratisAnual` grátis (padrão 2).
 * `mensalCentavos` é a receita recorrente mensal equivalente (MRR), sem implantação.
 */
export function calcularFatura(e: EntradaFatura): {
  itens: ItemFatura[];
  valorCentavos: number;
  mensalCentavos: number;
} {
  const meses = e.ciclo === 'anual' ? 12 - e.mesesGratisAnual : 1;
  const sufixo = e.ciclo === 'anual' ? ` (anual, ${12 - e.mesesGratisAnual} meses pagos)` : '';
  const recorrentes: ItemFatura[] = [
    {
      tipo: 'plano',
      codigo: e.plano.codigo,
      descricao: `Plano ${e.plano.nome}${sufixo}`,
      valorCentavos: e.plano.precoMensalCentavos * meses,
    },
  ];
  const extras = Math.max(0, e.filiais - 1);
  if (extras > 0)
    recorrentes.push({
      tipo: 'filial',
      codigo: 'filial_adicional',
      descricao: `${extras} filial${extras > 1 ? 'is' : ''} adicional${extras > 1 ? 'is' : ''}${sufixo}`,
      valorCentavos: e.plano.precoFilialAdicionalCentavos * extras * meses,
    });
  for (const a of e.adicionais) {
    const qtd = a.porFilial ? e.filiais : 1;
    recorrentes.push({
      tipo: 'adicional',
      codigo: a.codigo,
      descricao: `${a.nome}${qtd > 1 ? ` × ${qtd}` : ''}${sufixo}`,
      valorCentavos: a.precoMensalCentavos * qtd * meses,
    });
  }
  const recorrente = recorrentes.reduce((s, i) => s + i.valorCentavos, 0);
  const itens = [...recorrentes];
  if (e.implantacaoCentavos > 0)
    itens.push({
      tipo: 'implantacao',
      codigo: 'implantacao',
      descricao: 'Taxa de implantação',
      valorCentavos: e.implantacaoCentavos,
    });
  return {
    itens,
    valorCentavos: itens.reduce((s, i) => s + i.valorCentavos, 0),
    mensalCentavos: Math.round(recorrente / (e.ciclo === 'anual' ? 12 : 1)),
  };
}

/**
 * Diferença proporcional ao subir de plano no meio do período já faturado: o que falta do período
 * pelo preço novo menos o preço antigo. Nunca negativa (descer de plano vale no próximo período).
 */
export function proporcionalTroca(e: {
  inicioPeriodo: string;
  fimPeriodo: string;
  hoje: string;
  valorPeriodoAtualCentavos: number;
  valorPeriodoNovoCentavos: number;
}): { centavos: number; diasRestantes: number; diasPeriodo: number } {
  const d = (x: string) => Date.parse(`${x}T00:00:00Z`) / 86_400_000;
  const diasPeriodo = Math.max(1, d(e.fimPeriodo) - d(e.inicioPeriodo) + 1);
  const diasRestantes = Math.min(diasPeriodo, Math.max(0, d(e.fimPeriodo) - d(e.hoje) + 1));
  const dif = e.valorPeriodoNovoCentavos - e.valorPeriodoAtualCentavos;
  return {
    centavos: dif > 0 ? Math.round((dif * diasRestantes) / diasPeriodo) : 0,
    diasRestantes,
    diasPeriodo,
  };
}

export const trocaPlanoSchema = z.object({ plano: z.string().trim().min(2).max(40) });
export const moduloSchema = z.object({ modulo: z.string().trim().min(2).max(40) });

/**
 * Total do plano montado (base pela faixa + módulos), com o mesmo cálculo da fatura.
 * `mensal`: quanto custa por mês; `anualPorMes`: o mesmo no anual (meses grátis diluídos).
 */
export function precoMontado(e: {
  plano: { precoMensalCentavos: number; precoFilialAdicionalCentavos: number };
  modulos: { precoMensalCentavos: number; porFilial: boolean }[];
  filiais?: number;
  mesesGratisAnual: number;
}): { mensalCentavos: number; anualCentavos: number; anualPorMesCentavos: number } {
  const filiais = Math.max(1, e.filiais ?? 1);
  const mensal =
    e.plano.precoMensalCentavos +
    e.plano.precoFilialAdicionalCentavos * (filiais - 1) +
    e.modulos.reduce((t, m) => t + m.precoMensalCentavos * (m.porFilial ? filiais : 1), 0);
  const anual = mensal * (12 - e.mesesGratisAnual);
  return {
    mensalCentavos: mensal,
    anualCentavos: anual,
    anualPorMesCentavos: Math.round(anual / 12),
  };
}

/** Período coberto pela fatura que vence em `inicio` (AAAA-MM-DD). */
export function periodoFatura(
  inicio: string,
  ciclo: Ciclo,
): { inicio: string; fim: string; proximo: string } {
  const [a, m, d] = inicio.split('-').map(Number) as [number, number, number];
  const meses = ciclo === 'anual' ? 12 : 1;
  const prox = new Date(Date.UTC(a, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(prox.getUTCFullYear(), prox.getUTCMonth() + 1, 0)).getUTCDate();
  prox.setUTCDate(Math.min(d, ultimo));
  const fim = new Date(prox.getTime() - 86_400_000);
  return { inicio, fim: fim.toISOString().slice(0, 10), proximo: prox.toISOString().slice(0, 10) };
}

export const configCobrancaSchema = z.object({
  diasAviso: z.number().int().min(0).max(30),
  diasTolerancia: z.number().int().min(0).max(90),
  mesesGratisAnual: z.number().int().min(0).max(6),
  diasVencimentoFatura: z.number().int().min(1).max(30),
  implantacaoPadraoCentavos: z.number().int().min(0).max(10_000_000),
  exclusividadeProspectDias: z.number().int().min(1).max(365),
});
export type ConfigCobranca = z.infer<typeof configCobrancaSchema>;

export const precoPlanoSchema = z.object({
  precoMensalCentavos: z.number().int().min(0).max(10_000_000),
  precoFilialAdicionalCentavos: z.number().int().min(0).max(10_000_000),
});
export const adicionalPlanoSchema = z.object({
  nome: z.string().trim().min(2).max(60),
  precoMensalCentavos: z.number().int().min(0).max(10_000_000),
  ativo: z.boolean(),
});
export const assinaturaCobrancaSchema = z.object({
  ciclo: z.enum(CICLOS),
  metodo: z.enum(METODOS_COBRANCA),
  implantacaoCentavos: z.number().int().min(0).max(10_000_000).optional(),
  proximoVencimento: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

// ─────────── Parceiros comerciais ───────────

export const normalizarCnpj = (v: string) => v.replace(/\D/g, '');

/** CNPJ com dígitos verificadores válidos. */
export function cnpjValido(v: string): boolean {
  const c = normalizarCnpj(v);
  if (!/^\d{14}$/.test(c) || /^(\d)\1{13}$/.test(c)) return false;
  const dv = (base: string) => {
    const pesos =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const s = base.split('').reduce((t, n, i) => t + Number(n) * pesos[i]!, 0);
    const r = s % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = dv(c.slice(0, 12));
  const d2 = dv(c.slice(0, 12) + d1);
  return c.endsWith(`${d1}${d2}`);
}

export const prospectSchema = z.object({
  nomeLoja: z.string().trim().min(2).max(120),
  cnpj: z.string().refine(cnpjValido, 'CNPJ inválido').transform(normalizarCnpj),
  cidade: z.string().trim().max(80).optional(),
  uf: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/)
    .transform((v) => v.toUpperCase())
    .optional(),
  contato: z.string().trim().max(80).optional(),
  telefone: z.string().trim().max(20).optional(),
  observacoes: z.string().trim().max(500).optional(),
});

export const parceiroCriarSchema = z.object({
  nome: z.string().trim().min(2).max(80),
  email: z.string().trim().toLowerCase().email(),
  telefone: z.string().trim().max(20).optional(),
  metaLojasMes: z.number().int().min(0).max(100).default(0),
});

export const regraParceiroSchema = z.object({
  parceiroId: z.string().uuid().nullable(),
  bonusAtivacaoCentavos: z.number().int().min(0).max(10_000_000),
  mensalidadesParaBonus: z.number().int().min(1).max(24),
  mesesDevolucaoBonus: z.number().int().min(0).max(24),
  percentualRecorrente: z.number().int().min(0).max(5_000),
  percentualImplantacao: z.number().int().min(0).max(10_000),
  percentualSitePremium: z.number().int().min(0).max(5_000),
  mesesRecorrenteAposSaida: z.number().int().min(0).max(60),
  vigenciaInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type RegraParceiro = z.infer<typeof regraParceiroSchema>;

export const NOME_TIPO_COMISSAO_PARCEIRO = {
  ativacao: 'Bônus de ativação',
  recorrente: 'Recorrente',
  implantacao: 'Implantação',
  site_premium: 'Site Premium',
  estorno_ativacao: 'Devolução do bônus',
} as const;
export type TipoComissaoParceiro = keyof typeof NOME_TIPO_COMISSAO_PARCEIRO;

export interface ComissaoCalculada {
  tipo: TipoComissaoParceiro;
  baseCentavos: number;
  valorCentavos: number;
  descricao: string;
}

const pct = (base: number, centesimos: number) => Math.round((base * centesimos) / 10_000);

/**
 * Comissões de UMA fatura paga, sobre o valor efetivamente recebido (proporcional por item):
 * recorrente sobre plano/filiais/adicionais, percentual próprio para Site Premium e implantação,
 * bônus de ativação ao completar N mensalidades pagas. Parceiro que saiu recebe só as recorrentes
 * por N meses após a saída.
 */
export function comissoesDaFatura(e: {
  itens: ItemFatura[];
  valorFaturaCentavos: number;
  valorPagoCentavos: number;
  regra: Omit<RegraParceiro, 'parceiroId' | 'vigenciaInicio'>;
  /** faturas pagas da loja contando esta */
  pagasDaLoja: number;
  /** meses desde a saída do parceiro (null = ativo) */
  mesesDesdeSaida: number | null;
  /** o bônus já foi lançado para este parceiro e loja */
  bonusJaLancado: boolean;
}): ComissaoCalculada[] {
  const ativo = e.mesesDesdeSaida === null;
  if (!ativo && e.mesesDesdeSaida! > e.regra.mesesRecorrenteAposSaida) return [];
  const proporcao = e.valorFaturaCentavos ? e.valorPagoCentavos / e.valorFaturaCentavos : 0;
  const soma = (f: (i: ItemFatura) => boolean) =>
    Math.round(e.itens.filter(f).reduce((s, i) => s + i.valorCentavos, 0) * proporcao);
  const premium = soma((i) => i.codigo === 'site_premium');
  const implantacao = soma((i) => i.tipo === 'implantacao');
  const recorrente = soma((i) => i.tipo !== 'implantacao' && i.codigo !== 'site_premium');
  const r: ComissaoCalculada[] = [];
  if (recorrente > 0 && e.regra.percentualRecorrente > 0)
    r.push({
      tipo: 'recorrente',
      baseCentavos: recorrente,
      valorCentavos: pct(recorrente, e.regra.percentualRecorrente),
      descricao: `${e.regra.percentualRecorrente / 100}% da mensalidade recebida`,
    });
  if (premium > 0 && e.regra.percentualSitePremium > 0)
    r.push({
      tipo: 'site_premium',
      baseCentavos: premium,
      valorCentavos: pct(premium, e.regra.percentualSitePremium),
      descricao: `${e.regra.percentualSitePremium / 100}% do Site Premium`,
    });
  if (ativo && implantacao > 0 && e.regra.percentualImplantacao > 0)
    r.push({
      tipo: 'implantacao',
      baseCentavos: implantacao,
      valorCentavos: pct(implantacao, e.regra.percentualImplantacao),
      descricao: `${e.regra.percentualImplantacao / 100}% da implantação`,
    });
  if (
    ativo &&
    !e.bonusJaLancado &&
    e.regra.bonusAtivacaoCentavos > 0 &&
    e.pagasDaLoja >= e.regra.mensalidadesParaBonus
  )
    r.push({
      tipo: 'ativacao',
      baseCentavos: 0,
      valorCentavos: e.regra.bonusAtivacaoCentavos,
      descricao: `Bônus após ${e.regra.mensalidadesParaBonus} mensalidades pagas`,
    });
  return r.filter((c) => c.valorCentavos !== 0);
}

/** Cancelou antes de N meses: o bônus de ativação volta (lançamento negativo). */
export function estornoDoBonus(e: {
  mesesAtivo: number;
  mesesDevolucaoBonus: number;
  bonusCentavos: number | null;
  estornoJaLancado: boolean;
}): number | null {
  if (!e.bonusCentavos || e.estornoJaLancado) return null;
  return e.mesesAtivo < e.mesesDevolucaoBonus ? -e.bonusCentavos : null;
}

/** Meses inteiros entre duas datas (para devolução de bônus e recorrente após saída). */
export function mesesEntre(de: Date, ate: Date): number {
  const m =
    (ate.getUTCFullYear() - de.getUTCFullYear()) * 12 + (ate.getUTCMonth() - de.getUTCMonth());
  return ate.getUTCDate() < de.getUTCDate() ? m - 1 : m;
}
